import React from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Users, Zap, Building2, MoreHorizontal,
  Eye, Pencil, Key, Trash2,
  UserX, UserCheck, Power, PowerOff, TrendingUp,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { createPageUrl } from "@/shared/utils";
import { CanEdit, CanDelete } from "@/shared/components/PermissionGate";
import EntityTable from "@/shared/components/shared/EntityTable";
import { EntityRow } from "@/shared/components/shared/EntityRow";
import { EntityStatusBadge } from "@/shared/components/shared/EntityStatusBadge";

/* ─────────────────────────────────────────────
   RESELLER ROW
───────────────────────────────────────────── */
const ResellerRow = React.memo(({
  item: reseller, index, t, engines = [],
  getMonthlyVolume, onStatusClick, onDeleteClick,
}) => {
  const merchantCount = reseller.merchantCount ?? 0;

  const monthlyVolume = getMonthlyVolume ? getMonthlyVolume(reseller.id) : 0;

  const cells = [
    {
      id: "reseller",
      content: (
        <div className="flex items-center gap-3 px-4">
          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
            <Users className="h-5 w-5 text-purple-600" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-slate-900 truncate">{reseller.name}</p>
            <p className="text-sm text-slate-500 truncate">{reseller.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: "id",
      content: <Badge variant="outline" className="font-mono text-xs">{reseller.id?.substring(0, 8)}</Badge>,
    },
    {
      id: "parent",
      content: reseller.createdByEngineId ? (
        <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50 text-[10px]">
          {t ? t("engine") : "Engine"}:{" "}
          {engines.find((e) => e.id === reseller.createdByEngineId)?.name || reseller.createdByEngineId?.substring(0, 8)}
        </Badge>
      ) : reseller.createdByAdminId ? (
        <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 text-[10px]">
          {t ? t("directAdmin") : "Admin"}
        </Badge>
      ) : (
        <span className="text-[10px] text-slate-400">Unknown</span>
      ),
    },
    { id: "status", content: <EntityStatusBadge status={reseller.status} t={t} /> },
    {
      id: "merchants",
      content: (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Building2 className="h-4 w-4 text-slate-400" />
          <span>{merchantCount}</span>
          {reseller.max_merchants && <span className="text-slate-400">/ {reseller.max_merchants}</span>}
        </div>
      ),
    },
    {
      id: "volume",
      content: (
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <span className="font-semibold text-slate-700">Rs{monthlyVolume.toLocaleString()}</span>
        </div>
      ),
    },
    {
      id: "created",
      content: (
        <span className="text-sm text-slate-500">
          {reseller.createdAt ? format(new Date(reseller.createdAt), "MMM d, yyyy") : "N/A"}
        </span>
      ),
    },
    {
      id: "actions",
      className: "text-right px-4",
      content: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
              <MoreHorizontal className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild>
              <Link to={createPageUrl("AdminResellerDetail") + `?id=${reseller.id}`} className="cursor-pointer">
                <Eye className="h-4 w-4 mr-2 text-slate-400" />{t ? t("viewDetails") : "View Details"}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={createPageUrl("AdminResellerForm") + `?id=${reseller.id}`} className="cursor-pointer">
                <Pencil className="h-4 w-4 mr-2 text-slate-400" />{t ? t("edit") : "Edit"}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onStatusClick?.(reseller)} className="cursor-pointer">
              {reseller.status?.toUpperCase() === "ACTIVE" ? (
                <><UserX className="h-4 w-4 mr-2 text-amber-500" />{t ? t("suspend") : "Suspend"}</>
              ) : (
                <><UserCheck className="h-4 w-4 mr-2 text-emerald-500" />{t ? t("activate") : "Activate"}</>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDeleteClick?.(reseller)} className="text-red-600 cursor-pointer">
              <Trash2 className="h-4 w-4 mr-2" />{t ? t("delete") : "Delete"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return <EntityRow cells={cells} index={index} />;
});
ResellerRow.displayName = "ResellerRow";

/* ─────────────────────────────────────────────
   ENGINE ROW
───────────────────────────────────────────── */
const EngineRow = React.memo(({
  item: engine, index, t,
  onStatusClick, onDeleteClick,
}) => {
  const resellerCount = engine.resellerCount ?? 0;
  const merchantCount = engine.merchantCount ?? 0;

  const cells = [
    {
      id: "engine",
      content: (
        <div className="flex items-center gap-3 px-4">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
            <Zap className="h-5 w-5 text-sky-600" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-slate-900 truncate">{engine.name}</p>
            <p className="text-sm text-slate-500 truncate">{engine.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: "code",
      className: "hidden sm:table-cell",
      content: <Badge variant="outline" className="font-mono text-xs">{engine.id?.substring(0, 8)}</Badge>,
    },
    { id: "status", content: <EntityStatusBadge status={engine.status} t={t} /> },
    {
      id: "resellers",
      className: "hidden md:table-cell",
      content: (
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <Users className="h-4 w-4 text-slate-400" /><span>{resellerCount}</span>
        </div>
      ),
    },
    {
      id: "merchants",
      className: "hidden md:table-cell",
      content: (
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <Building2 className="h-4 w-4 text-slate-400" /><span>{merchantCount}</span>
        </div>
      ),
    },
    {
      id: "created",
      className: "hidden lg:table-cell",
      content: (
        <span className="text-sm text-slate-500">
          {engine.createdAt ? format(new Date(engine.createdAt), "MMM d, yyyy") : "N/A"}
        </span>
      ),
    },
    {
      id: "actions",
      className: "text-right px-4",
      content: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
              <MoreHorizontal className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild>
              <Link to={createPageUrl("AdminEngineDetail") + `?id=${engine.id}`} className="cursor-pointer">
                <Eye className="h-4 w-4 mr-2 text-slate-400" />{t ? t("view") : "View"}
              </Link>
            </DropdownMenuItem>
            <CanEdit resource="engines">
              <DropdownMenuItem asChild>
                <Link to={createPageUrl("AdminEngineForm") + `?id=${engine.id}`} className="cursor-pointer">
                  <Pencil className="h-4 w-4 mr-2 text-slate-400" />{t ? t("edit") : "Edit"}
                </Link>
              </DropdownMenuItem>
            </CanEdit>
            <CanEdit resource="engines">
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onStatusClick?.(engine)} className="cursor-pointer">
                {engine.status === "ACTIVE" ? (
                  <><PowerOff className="h-4 w-4 mr-2 text-amber-500" />{t ? t("suspend") : "Suspend"}</>
                ) : (
                  <><Power className="h-4 w-4 mr-2 text-emerald-500" />{t ? t("activate") : "Activate"}</>
                )}
              </DropdownMenuItem>
            </CanEdit>
            <CanDelete resource="engines">
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onDeleteClick?.(engine)} className="text-red-600 cursor-pointer">
                <Trash2 className="h-4 w-4 mr-2" />{t ? t("delete") : "Delete"}
              </DropdownMenuItem>
            </CanDelete>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return <EntityRow cells={cells} index={index} />;
});
EngineRow.displayName = "EngineRow";

/* ─────────────────────────────────────────────
   MERCHANT ROW
───────────────────────────────────────────── */
const MerchantRow = React.memo(({
  item: merchant, index, t, resellers = [], engines = [],
  onEdit, onToggleStatus, onDelete,
}) => {
  const formatFee = () => {
    const rateStr = merchant.fee_rate ?? merchant.feeRate;
    if (rateStr == null) return "N/A";
    const type = (merchant.fee_type || merchant.feeType || "PERCENTAGE").toUpperCase();
    if (type === "FIXED") return `${rateStr} ${merchant.currency || ""}`.trim();
    return `${rateStr}%`;
  };

  const renderParentBadge = () => {
    if (merchant.createdByResellerId) {
      const reseller = resellers.find((r) => r.id === merchant.createdByResellerId);
      return (
        <Badge variant="outline" className="border-purple-300 text-purple-700 bg-purple-50 text-[10px]">
          Reseller: {reseller?.name || merchant.createdByResellerId?.substring(0, 8)}
        </Badge>
      );
    }
    if (merchant.createdByEngineId) {
      const engine = engines.find((e) => e.id === merchant.createdByEngineId);
      return (
        <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50 text-[10px]">
          Engine: {engine?.name || merchant.createdByEngineId?.substring(0, 8)}
        </Badge>
      );
    }
    if (merchant.createdByAdminId) {
      return (
        <Badge variant="outline" className="border-slate-300 text-slate-700 bg-slate-50 text-[10px]">
          {t ? t("directAdmin") : "Admin"}
        </Badge>
      );
    }
    return <span className="text-[10px] text-slate-400">Unknown</span>;
  };

  const cells = [
    {
      id: "merchant",
      content: (
        <div className="flex items-center gap-3 px-4">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-blue-500" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 text-sm truncate">{merchant.name}</p>
            <p className="text-xs text-slate-500 truncate">{merchant.email || "No Email"}</p>
          </div>
        </div>
      ),
    },
    {
      id: "code",
      content: <Badge variant="outline" className="font-mono text-[11px] text-slate-600 bg-slate-50/50">{merchant.code || "N/A"}</Badge>,
    },
    { id: "parent", content: renderParentBadge() },
    { id: "status", content: <EntityStatusBadge status={merchant.status} t={t} /> },
    {
      id: "features",
      content: (
        <div className="flex gap-1 flex-wrap">
          {(merchant.depositEnabled || merchant.depositCommission != null) && (
            <Badge variant="outline" className="text-[10px] h-5 border-blue-100 text-blue-600">IN</Badge>
          )}
          {(merchant.withdrawalEnabled || merchant.withdrawalCommission != null) && (
            <Badge variant="outline" className="text-[10px] h-5 border-amber-100 text-amber-600">OUT</Badge>
          )}
        </div>
      ),
    },
    { id: "fee", content: <span className="text-sm font-medium text-slate-600">{formatFee()}</span> },
    { id: "settlement", content: <span className="text-xs text-slate-500">{merchant.settlementPeriod || "N/A"}</span> },
    {
      id: "currency",
      content: <Badge variant="outline" className="text-[10px] font-bold border-slate-200">{merchant.currency || "PKR"}</Badge>,
    },
    {
      id: "actions",
      className: "text-right px-4",
      content: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
              <MoreHorizontal className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem asChild>
              <Link to={createPageUrl("AdminMerchantDetail") + `?id=${merchant.id}`}>
                <Eye className="h-4 w-4 mr-2 text-slate-400" />{t ? t("viewDetails") : "View Details"}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit?.(merchant)}>
              <Pencil className="h-4 w-4 mr-2 text-slate-400" />{t ? t("edit") : "Edit"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit?.(merchant)}>
              <Key className="h-4 w-4 mr-2 text-slate-400" />{t ? t("viewApiKeys") : "API Keys"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onToggleStatus?.(merchant)}>
              {merchant.status?.toUpperCase() === "ACTIVE" ? (
                <><PowerOff className="h-4 w-4 mr-2 text-amber-500" />{t ? t("suspend") : "Suspend"}</>
              ) : (
                <><Power className="h-4 w-4 mr-2 text-emerald-500" />{t ? t("activate") : "Activate"}</>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete?.(merchant)} className="text-red-600">
              <Trash2 className="h-4 w-4 mr-2" />{t ? t("delete") : "Delete"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return <EntityRow cells={cells} index={index} />;
});
MerchantRow.displayName = "MerchantRow";

/* ─────────────────────────────────────────────
   CONFIGS
───────────────────────────────────────────── */
const RESELLER_HEADERS = (t) => [
  { id: "reseller", label: t ? t("reseller") : "Reseller" },
  { id: "id", label: "ID" },
  { id: "parent", label: t ? t("parent") : "Parent" },
  { id: "status", label: t ? t("status") : "Status" },
  { id: "merchants", label: t ? t("merchants") : "Merchants" },
  { id: "volume", label: t ? t("monthlyVolume") : "Volume" },
  { id: "created", label: t ? t("created") : "Created" },
  { id: "actions", label: "" },
];

const ENGINE_HEADERS = (t) => [
  { id: "engine", label: t ? t("engine") : "Engine", },
  { id: "code", label: t ? t("code") : "Code", className: "hidden sm:table-cell" },
  { id: "status", label: t ? t("status") : "Status", },
  { id: "resellers", label: t ? t("resellers") : "Resellers", className: "hidden md:table-cell" },
  { id: "merchants", label: t ? t("merchants") : "Merchants", className: "hidden md:table-cell" },
  { id: "created", label: t ? t("created") : "Created", className: "hidden lg:table-cell" },
  { id: "actions", label: "" },
];

const MERCHANT_HEADERS = (t) => [
  { id: "merchant", label: t ? t("merchant") : "Merchant" },
  { id: "code", label: t ? t("code") : "Code" },
  { id: "parent", label: t ? t("parent") : "Parent" },
  { id: "status", label: t ? t("status") : "Status" },
  { id: "features", label: t ? t("features") : "Features" },
  { id: "fee", label: t ? t("fee") : "Fee" },
  { id: "settlement", label: t ? t("settlement") : "Settlement" },
  { id: "currency", label: t ? t("currency") : "Currency" },
  { id: "actions", label: "" },
];

/* ─────────────────────────────────────────────
   MAIN EXPORT: EntityPanel
───────────────────────────────────────────── */
export default function EntityPanel({
  entityType,   // "reseller" | "engine" | "merchant"
  data = [],
  isLoading = false,
  t,
  // Reseller-specific
  engines = [],
  getMerchantCount,
  getMonthlyVolume,
  onStatusClick,
  onDeleteClick,
  // Engine-specific
  resellers = [],
  merchants = [],
  // Merchant-specific
  onEdit,
  onToggleStatus,
  onDelete,
}) {
  const configs = {
    reseller: {
      headers: RESELLER_HEADERS(t),
      emptyMessage: t ? t("noResellersFound") : "No resellers found",
      firstCol: "reseller",
      renderRow: (item, index) => (
        <ResellerRow
          key={item.id || index}
          item={item}
          index={index}
          t={t}
          engines={engines}
          getMonthlyVolume={getMonthlyVolume}
          onStatusClick={onStatusClick}
          onDeleteClick={onDeleteClick}
        />
      ),
    },
    engine: {
      headers: ENGINE_HEADERS(t),
      emptyMessage: t ? t("noEnginesFound") : "No engines found",
      firstCol: "engine",
      renderRow: (item, index) => (
        <EngineRow
          key={item.id || index}
          item={item}
          index={index}
          t={t}
          onStatusClick={onStatusClick}
          onDeleteClick={onDeleteClick}
        />
      ),
    },
    merchant: {
      headers: MERCHANT_HEADERS(t),
      emptyMessage: t ? t("noMerchantsFound") : "No merchants found",
      firstCol: "merchant",
      renderRow: (item, index) => (
        <MerchantRow
          key={item.id || index}
          item={item}
          index={index}
          t={t}
          resellers={resellers}
          engines={engines}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
          onDelete={onDelete}
        />
      ),
    },
  };

  const config = configs[entityType];
  if (!config) return null;

  const skeletonColumns = config.headers.map((h) => ({
    header: h.label,
    renderSkeleton: h.id === config.firstCol ? () => (
      <div className="flex items-center gap-3 px-4">
        <div className="w-10 h-10 rounded-xl bg-slate-100 animate-pulse" />
        <div className="space-y-1">
          <div className="h-3 w-28 bg-slate-100 rounded animate-pulse" />
          <div className="h-2 w-36 bg-slate-50 rounded animate-pulse" />
        </div>
      </div>
    ) : null,
  }));

  return (
    <EntityTable
      headers={config.headers}
      data={data}
      isLoading={isLoading}
      emptyMessage={config.emptyMessage}
      skeletonColumns={skeletonColumns}
      renderRow={config.renderRow}
    />
  );
}
